import React from "react";

// components
import SelectInput from "../../../components/elements/form/SelectInput";
import TextBox from "../../../components/elements/form/TextBox";
import Button from "../../../components/elements/form/Button";

// state
import { get_countries, get_states } from "../../../service/api/locations";
import { create_request_service } from "../../../service/api/requests";
import { useAppDispatch, useAppSelector } from "../../../store";
import { clearStates } from "../../../store/slices/locations";
import {
  IServiceRequestData,
  IServiceRequestDataError,
} from "../../../store/@types/requests";
import Modal from "../../../components/ui/modal";

type IMakeRequest = {
  id: string;
  visible: boolean;
  onFinish: () => void;
  type: "new" | "renew";
};

const MakeRequest: React.FC<IMakeRequest> = ({
  id,
  visible,
  onFinish,
  type,
}) => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector(({ service }) => service.loading);
  const {
    countries,
    states,
    loading: locationsLoading,
  } = useAppSelector(({ locations }) => locations);
  const [data, dataSet] = React.useState<IServiceRequestData>({
    service_id: 0,
    state_id: 0,
    country_id: 0,
    others: "",
    type: "new",
  });

  const [error, errorSet] = React.useState<IServiceRequestDataError>({
    state_id: "",
    country_id: "",
    others: "",
    service_id: "",
    type: "",
  });

  const setId = React.useCallback(() => {
    const serviceId = Number(id);
    dataSet((prev) => ({
      ...prev,
      service_id:
        Number.isSafeInteger(serviceId) && serviceId > 0 ? serviceId : 0,
      type,
    }));
  }, [id, type]);

  const onLoad = React.useCallback(() => {
    if (countries.length === 0) {
      void get_countries(dispatch);
    }
  }, [countries.length, dispatch]);

  React.useEffect(() => {
    if (!visible) return;
    onLoad();
    setId();
  }, [onLoad, setId, visible]);

  const onSubmit = async () => {
    const validationErrors: IServiceRequestDataError = {
      service_id: data.service_id > 0 ? "" : "warning",
      country_id: data.country_id > 0 ? "" : "warning",
      state_id: data.state_id > 0 ? "" : "warning",
      others: "",
      type: "",
    };
    errorSet(validationErrors);

    if (Object.values(validationErrors).some(Boolean)) {
      return;
    }

    const response = await create_request_service(dispatch, data);
    if (response) onFinish?.();
  };

  return (
    <Modal
      open={visible}
      onClose={() => {
        dataSet({
          service_id: 0,
          state_id: 0,
          country_id: 0,
          others: "",
          type: "new",
        });
        errorSet({
          state_id: "",
          country_id: "",
          others: "",
          service_id: "",
          type: "",
        });
        dispatch(clearStates());
        onFinish();
      }}
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <SelectInput
            label="Country"
            labelClassName="text-dark"
            items={countries.map(({ id, name }) => ({
              id: String(id),
              name,
            }))}
            name="country"
            value={data.country_id}
            status={error.country_id}
            loading={locationsLoading && countries.length === 0}
            onChange={(e) => {
              const countryId = Number(e.target.value) || 0;
              dispatch(clearStates());
              dataSet((prev) => ({
                ...prev,
                country_id: countryId,
                state_id: 0,
              }));
              errorSet((prev) => ({
                ...prev,
                country_id: "",
                state_id: "",
              }));
              if (countryId > 0) {
                void get_states(dispatch, countryId);
              }
            }}
            className="capitalize"
          />

          <SelectInput
            label="State"
            labelClassName="text-dark"
            items={states.map(({ id, name }) => ({
              id: String(id),
              name,
            }))}
            name="state_id"
            status={error.state_id}
            value={data.state_id}
            loading={locationsLoading && data.country_id > 0}
            onChange={(e) => {
              dataSet((prev) => ({
                ...prev,
                state_id: Number(e.target.value) || 0,
              }));
              errorSet((prev) => ({
                ...prev,
                state_id: "",
              }));
            }}
          />
        </div>
        <div className="mt-3 md:mt-0">
          <TextBox
            label="Others"
            labelClassName="text-dark"
            name="others"
            placeholder="Enter addition or exception"
            status={error.others}
            value={data.others}
            rowsValue={3}
            onChange={(e) => {
              dataSet((prev) => ({ ...prev, others: e.target.value }));
              errorSet((prev) => ({ ...prev, others: "" }));
            }}
          />
        </div>

        <Button
          title="Make Request"
          onHandler={onSubmit}
          loading={loading}
          className="w-full text-[white] bg-primary-500 hover:bg-primary-800 transition-colors ease-out duration-300 rounded-xl"
        />
      </div>
    </Modal>
  );
};

export default MakeRequest;
